/**
 * storeBilling — the ONE file that talks to the store's own payment sheet
 * (plan 032). Nothing else imports `@capgo/native-purchases`.
 *
 * What is sold here is SSi Premium, monthly and annual, and the India English
 * courses, one store product per course (plan 043, @ssi/core storeCourseCatalog).
 * Family, Voice, school prices and every other course need their own store
 * products and server mapping; until they have them they stay off in the apps.
 *
 * THE SERVER DECIDES. The plugin hands back a signed transaction (iOS JWS) or a
 * purchase token (Android); this file posts it to /api/store/apple-verify or
 * /api/store/play-verify and says 'granted' only on a 200. The server re-reads
 * the purchase from Apple or Google, writes the grant, and acknowledges Play
 * purchases itself. The plugin's auto-acknowledge is off: on Android an
 * unacknowledged purchase the server never verified is refunded by Google
 * after three days, and on iOS a transaction is finished only once the server
 * has it. A 200 whose `owned` is false means the purchase belongs to another
 * SSi account: the server kept that owner, and this caller got nothing. Only
 * Restore asks the server to move a purchase to this account (plan 033).
 *
 * THE PLUGIN IS LOADED ONLY WHERE IT CAN WORK. Every entry point checks
 * storeBillingLive() first and imports the plugin lazily, so the web never
 * loads a byte of it and an older shell, which has no plugin, never asks.
 */
import type { PURCHASE_TYPE, Product, Transaction } from '@capgo/native-purchases'
import { INDIA_STORE_COURSES, STORE_COURSE_BASE_PLANS, courseOfStoreProduct, iosStoreCoursePeriod } from '@ssi/core'
import { platform } from './capabilities'
import { storeBillingLive } from './paymentRoute'
import { resolveSupabase } from '@/composables/schools/client'
import { useSharedSubscription } from '@/composables/useSubscription'
import { useSharedUserEntitlements } from '@/composables/useUserEntitlements'
import { noteEntitlementRefused } from '@/composables/useVoiceEntitlement'
import { invalidateSignedInBoot } from '@/playback/signedInBoot'

export type StorePeriod = 'monthly' | 'annual'
export type StoreProduct = { id: string; period: StorePeriod; priceLabel: string }
export type StoreCourseProduct = { courseCode: string; period: StorePeriod; priceLabel: string }
/**
 * 'owned-elsewhere': the store took the payment, but the server says the
 * purchase already belongs to a different SSi account. Buying never moves it;
 * Restore in Settings can (plan 033), so the buyer is told which it is.
 * 'unconfirmed': the server check ran out of time, or its answer could not be
 * read. The server may still grant it, so the buyer is told it may yet go
 * through and that Restore purchases picks it up, not that it failed.
 * 'unavailable': refused before the payment sheet opened, because the store no
 * longer sells that period at a plain price or did not answer when asked again.
 * Nothing was bought (plan 040).
 */
export type StoreBuyResult = 'granted' | 'cancelled' | 'pending' | 'failed' | 'owned-elsewhere' | 'unconfirmed' | 'unavailable'

/**
 * The store product ids, in one place. iOS: one product per period in the
 * "SSi Premium" subscription group. Android: one subscription, `premium`, with
 * a base plan per period.
 */
export const STORE_PRODUCTS = {
  ios: {
    monthly: 'com.saysomethingin.app.premium.monthly',
    annual: 'com.saysomethingin.app.premium.annual',
  },
  android: {
    subscription: 'premium',
    basePlans: { monthly: 'monthly', annual: 'annual' },
  },
} as const

const SUBS = 'subs' as PURCHASE_TYPE

/**
 * Resolves to the MODULE, never to the plugin object. A Capacitor plugin proxy
 * answers every property name, `then` included, so awaiting it, or returning it
 * from an async function, calls a native `then()` that never settles and the
 * caller hangs forever. Read `.NativePurchases` off the result without awaiting it.
 */
let pluginModule: Promise<typeof import('@capgo/native-purchases')> | null = null
function plugin(): Promise<typeof import('@capgo/native-purchases')> {
  // One import, shared, so reads queued together use the same module. A failed
  // load is forgotten, so the next call tries again.
  pluginModule ??= import('@capgo/native-purchases').catch((err) => {
    pluginModule = null
    throw err
  })
  return pluginModule
}

/**
 * A store read that never answers must not hold a spinner for ever. Used for the
 * calls that need no tap from the buyer: product prices and the purchase list.
 * The payment sheet, Restore's Apple ID prompt and the manage page wait on the
 * person, so they get no limit.
 */
export const STORE_READ_TIMEOUT_MS = 15000

function withinStoreTimeout<T>(call: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const limit = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('store read timed out')), STORE_READ_TIMEOUT_MS)
  })
  return Promise.race([call, limit]).finally(() => clearTimeout(timer))
}

/**
 * One product query at a time. The Android plugin keeps a single billing
 * client and each getProducts closes and reopens it, so a second query that
 * starts while the first is in flight kills the first ("Product not found"),
 * and its README says never to run them concurrently. The picker reads Premium
 * and the India courses, and a purchase re-reads before the sheet opens, so
 * every getProducts here waits for the one before it. The chain moves on when
 * the previous read settles or hits its time limit.
 */
let productQuery: Promise<unknown> = Promise.resolve()

function queryProducts(
  store: Awaited<ReturnType<typeof plugin>>['NativePurchases'],
  productIdentifiers: string[],
): Promise<{ products: Product[] }> {
  const run = productQuery.then(
    () => withinStoreTimeout(store.getProducts({ productIdentifiers, productType: SUBS })),
  ) as Promise<{ products: Product[] }>
  productQuery = run.catch(() => undefined)
  return run
}

function storeOs(): 'ios' | 'android' | null {
  const os = platform().os
  return os === 'ios' || os === 'android' ? os : null
}

function periodOfIosProduct(id: string): StorePeriod | null {
  if (id === STORE_PRODUCTS.ios.monthly) return 'monthly'
  if (id === STORE_PRODUCTS.ios.annual) return 'annual'
  return null
}

/** Is this transaction one of ours, Premium or an India course? Anything else is not this file's to verify. */
function isSsiTransaction(tx: Transaction): boolean {
  if (courseOfStoreProduct(tx.productIdentifier) !== null) return true
  return storeOs() === 'ios'
    ? periodOfIosProduct(tx.productIdentifier) !== null
    : tx.productIdentifier === STORE_PRODUCTS.android.subscription
}

async function authToken(): Promise<string | null> {
  const client = resolveSupabase()
  if (!client) return null
  try {
    const { data: { session } } = await client.auth.getSession()
    return session?.access_token ?? null
  } catch {
    return null
  }
}

/**
 * The server check gets a limit too. The buyer watches a confirming screen
 * while it runs, and a request that never answers must not hold it for ever.
 * An unanswered check counts as not processed: the transaction stays
 * unfinished, so Restore or the next reconcile tries it again. The limit is
 * cleared the moment the server answers.
 */
export const STORE_VERIFY_TIMEOUT_MS = 30000

/**
 * Hand one transaction to the server. `processed` on a 200 — the server's word
 * that it verified the purchase with the store and wrote the grant. `owned`
 * only when that grant is this caller's.
 */
async function verifyWithServer(
  tx: Transaction,
  opts: { transfer?: boolean } = {},
): Promise<{ processed: boolean; owned: boolean; unconfirmed?: boolean }> {
  const refused = { processed: false, owned: false }
  const os = storeOs()
  if (!os) return refused
  const body = os === 'ios'
    ? (tx.jwsRepresentation ? { signedTransaction: tx.jwsRepresentation } : null)
    : (tx.purchaseToken ? { purchaseToken: tx.purchaseToken } : null)
  if (!body) return refused
  const payload = opts.transfer === true ? { ...body, transfer: true } : body
  const token = await authToken()
  if (!token) return refused
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), STORE_VERIFY_TIMEOUT_MS)
  try {
    const res = await fetch(os === 'ios' ? '/api/store/apple-verify' : '/api/store/play-verify', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
    if (res.status !== 200) return refused
    const answer = await res.json().catch(() => null) as { owned?: unknown } | null
    // A 200 that cannot be read, a proxy page say, is no word on the grant either way.
    if (typeof answer?.owned !== 'boolean') return { ...refused, unconfirmed: true }
    return { processed: true, owned: answer.owned }
  } catch {
    return controller.signal.aborted ? { ...refused, unconfirmed: true } : refused
  } finally {
    clearTimeout(timer)
  }
}

/**
 * iOS only: finish a transaction the server has processed, so StoreKit stops
 * redelivering it. Android purchases are acknowledged by play-verify itself.
 */
async function finishIfProcessed(tx: Transaction, processed: boolean): Promise<void> {
  if (!processed || storeOs() !== 'ios' || !tx.transactionId) return
  try {
    await (await plugin()).NativePurchases.acknowledgePurchase({ purchaseToken: tx.transactionId })
  } catch {
    // Already finished, or StoreKit no longer lists it: nothing is left to finish.
  }
}

/**
 * The server has written a grant: re-read everything that decides a padlock.
 * The grant lands as a user_entitlements row, and the paywall lowers from the
 * entitlements list, not from /api/subscription, so both are re-read. The
 * sign-in boot snapshot predates the purchase and would answer both GETs with
 * the old "no" until its TTL ran out, so it is dropped first (as
 * usePendingPurchase does). noteEntitlementRefused re-reads the voice flag and
 * the entitlement grid.
 */
async function refreshAfterGrant(): Promise<void> {
  invalidateSignedInBoot()
  await Promise.all([
    useSharedSubscription().refresh().catch(() => undefined),
    useSharedUserEntitlements().refresh().catch(() => undefined),
  ])
  try {
    noteEntitlementRefused()
  } catch {
    // The voice and grid re-read is best effort; the two lists above are what lower the wall.
  }
}

/**
 * How the plugin says "the buyer closed the sheet" or "the bank has not said
 * yet". iOS rejects with "User cancelled" / "Transaction pending"; Android with
 * code USER_CANCELED, or "Purchase is pending".
 */
function classifyPurchaseError(err: unknown): StoreBuyResult {
  const e = err as { code?: unknown; message?: unknown } | null
  const code = typeof e?.code === 'string' ? e.code : ''
  const message = typeof e?.message === 'string' ? e.message : ''
  if (code === 'USER_CANCELED' || /cancel/i.test(message)) return 'cancelled'
  if (/pending/i.test(message)) return 'pending'
  return 'failed'
}

/**
 * A product with a free trial or an introductory price is not sold in the apps
 * (plan 040). The paywall and the padlock show only the recurring price; the
 * plugin buys a Play subscription through the FIRST offer Google lists for its
 * base plan and ignores an offer token, and StoreKit applies an introductory
 * offer by itself, so a buyer could be put on terms nobody showed them. Android
 * lists each offer as its own row with an offerId; iOS carries an introductory
 * offer on the product as introductoryPrice. Before adding a trial or an intro
 * price in Play Console or App Store Connect: show its terms on the paywall AND
 * buy it on purpose (an offer token on Android, which is a plugin change and a
 * new store build), then lift this for that plan.
 */
function carriesOffer(p: Product): boolean {
  return !!p.offerId || !!p.introductoryPrice
}

/** The Premium periods the store sells at a plain price, read now. Throws when the store does not answer. */
async function readSellableProducts(os: 'ios' | 'android'): Promise<StoreProduct[]> {
  const store = (await plugin()).NativePurchases
  const ids = os === 'ios'
    ? [STORE_PRODUCTS.ios.monthly, STORE_PRODUCTS.ios.annual]
    : [STORE_PRODUCTS.android.subscription]
  const { products } = await queryProducts(store, ids)
  const byPeriod = new Map<StorePeriod, StoreProduct>()
  const offered = new Set<StorePeriod>()
  for (const p of products as Product[]) {
    // Android lists a subscription once per offer, keyed by base plan id.
    const period: StorePeriod | null = os === 'ios'
      ? periodOfIosProduct(p.identifier)
      : p.identifier === STORE_PRODUCTS.android.basePlans.monthly ? 'monthly'
        : p.identifier === STORE_PRODUCTS.android.basePlans.annual ? 'annual' : null
    if (!period) continue
    if (carriesOffer(p)) { offered.add(period); continue }
    if (!p.priceString) continue
    byPeriod.set(period, { id: os === 'ios' ? p.identifier : `${STORE_PRODUCTS.android.subscription}:${p.identifier}`, period, priceLabel: p.priceString })
  }
  if (offered.size) console.warn('[storeBilling] not sold here, the store lists a trial or intro offer on:', [...offered].join(', '))
  return (['monthly', 'annual'] as const).filter((k) => !offered.has(k)).flatMap((k) => byPeriod.get(k) ?? [])
}

/** Premium, monthly and annual, at the store's own prices. Empty when billing is off or the store does not answer. */
export async function loadStoreProducts(): Promise<StoreProduct[]> {
  if (!storeBillingLive()) return []
  const os = storeOs()
  if (!os) return []
  try {
    return await readSellableProducts(os)
  } catch {
    return []
  }
}

/**
 * Open the store's payment sheet for Premium. The learner id goes in as the
 * account token (iOS appAccountToken, Android obfuscatedAccountId), so the
 * server can tie the purchase to this account. Only Restore moves it later.
 *
 * `onPaid` fires the moment the store sheet closes on a payment, before the
 * server check, which takes several seconds. The caller shows the wait then,
 * so the buyer does not look at the plans they have just paid for. It gets the
 * payment reference support can look up: the App Store transaction id, or the
 * Google Play order id. Android's transactionId is the long purchase token.
 */
export async function buyPremium(
  period: StorePeriod,
  learnerId: string,
  onPaid?: (reference: string | null) => void,
): Promise<StoreBuyResult> {
  if (!storeBillingLive()) return 'failed'
  const os = storeOs()
  if (!os || !learnerId) return 'failed'
  // Asked again at the moment of purchase, not only when the prices were drawn
  // (plan 040): the padlock buys monthly without the list, and an offer can
  // appear between drawing the list and the tap. No answer sells nothing.
  let sellable: StoreProduct[]
  try {
    sellable = await readSellableProducts(os)
  } catch {
    return 'unavailable'
  }
  if (!sellable.some((p) => p.period === period)) return 'unavailable'
  let tx: Transaction
  try {
    const store = (await plugin()).NativePurchases
    tx = await store.purchaseProduct(os === 'ios'
      ? { productIdentifier: STORE_PRODUCTS.ios[period], productType: SUBS, appAccountToken: learnerId, autoAcknowledgePurchases: false }
      : {
          productIdentifier: STORE_PRODUCTS.android.subscription,
          planIdentifier: STORE_PRODUCTS.android.basePlans[period],
          productType: SUBS,
          appAccountToken: learnerId,
          autoAcknowledgePurchases: false,
        })
  } catch (err) {
    return classifyPurchaseError(err)
  }
  return settleStorePurchase(tx, os, onPaid)
}

/** After the payment sheet closes on a payment: show the wait, check with the server, finish, re-read. */
async function settleStorePurchase(
  tx: Transaction, os: 'ios' | 'android', onPaid?: (reference: string | null) => void,
): Promise<StoreBuyResult> {
  try {
    onPaid?.((os === 'android' ? tx.orderId : tx.transactionId) ?? null)
  } catch {
    // The buyer has paid. A broken screen must not stop the server check.
  }
  const { processed, owned, unconfirmed } = await verifyWithServer(tx)
  await finishIfProcessed(tx, processed)
  if (!processed) return unconfirmed ? 'unconfirmed' : 'failed'
  if (!owned) return 'owned-elsewhere'
  await refreshAfterGrant()
  return 'granted'
}

/** The India course periods the store sells at a plain price, read now. Throws when the store does not answer. */
async function readSellableCourseProducts(os: 'ios' | 'android'): Promise<StoreCourseProduct[]> {
  const store = (await plugin()).NativePurchases
  const ids = os === 'ios'
    ? INDIA_STORE_COURSES.flatMap((c) => [c.ios.monthly, c.ios.annual])
    : INDIA_STORE_COURSES.map((c) => c.play)
  const { products } = await queryProducts(store, ids)
  const plain = new Map<string, StoreCourseProduct>()
  const offered = new Set<string>()
  for (const p of products as Product[]) {
    // iOS: identifier is the product id. Android: identifier is the BASE PLAN id
    // and planIdentifier is the product id.
    const courseCode = courseOfStoreProduct(os === 'ios' ? p.identifier : p.planIdentifier)
    const period: StorePeriod | null = os === 'ios'
      ? iosStoreCoursePeriod(p.identifier)
      : p.identifier === STORE_COURSE_BASE_PLANS.monthly ? 'monthly'
        : p.identifier === STORE_COURSE_BASE_PLANS.annual ? 'annual' : null
    if (!courseCode || !period) continue
    const key = `${courseCode}:${period}`
    if (carriesOffer(p)) { offered.add(key); continue }
    if (p.priceString) plain.set(key, { courseCode, period, priceLabel: p.priceString })
  }
  if (offered.size) console.warn('[storeBilling] not sold here, the store lists a trial or intro offer on:', [...offered].join(', '))
  // Catalogue order, monthly before annual.
  return INDIA_STORE_COURSES.flatMap((c) => (['monthly', 'annual'] as const)
    .map((period) => `${c.courseCode}:${period}`)
    .filter((key) => !offered.has(key))
    .flatMap((key) => plain.get(key) ?? []))
}

/** The India courses the store sells here, at its own prices. Empty when billing is off or the store does not answer. */
export async function loadStoreCourseProducts(): Promise<StoreCourseProduct[]> {
  if (!storeBillingLive()) return []
  const os = storeOs()
  if (!os) return []
  try {
    return await readSellableCourseProducts(os)
  } catch {
    return []
  }
}

/**
 * Open the store's payment sheet for one India course (plan 043). The same rules
 * as buyPremium: the learner id is the account token, the store is asked again
 * at the moment of purchase, and only the server's 200 counts.
 */
export async function buyStoreCourse(
  courseCode: string,
  period: StorePeriod,
  learnerId: string,
  onPaid?: (reference: string | null) => void,
): Promise<StoreBuyResult> {
  if (!storeBillingLive()) return 'failed'
  const os = storeOs()
  const course = INDIA_STORE_COURSES.find((c) => c.courseCode === courseCode)
  if (!os || !learnerId || !course) return 'failed'
  let sellable: StoreCourseProduct[]
  try {
    sellable = await readSellableCourseProducts(os)
  } catch {
    return 'unavailable'
  }
  if (!sellable.some((p) => p.courseCode === courseCode && p.period === period)) return 'unavailable'
  let tx: Transaction
  try {
    const store = (await plugin()).NativePurchases
    tx = await store.purchaseProduct(os === 'ios'
      ? { productIdentifier: course.ios[period], productType: SUBS, appAccountToken: learnerId, autoAcknowledgePurchases: false }
      : {
          productIdentifier: course.play,
          planIdentifier: STORE_COURSE_BASE_PLANS[period],
          productType: SUBS,
          appAccountToken: learnerId,
          autoAcknowledgePurchases: false,
        })
  } catch (err) {
    return classifyPurchaseError(err)
  }
  return settleStorePurchase(tx, os, onPaid)
}

/**
 * Restore Purchases (Apple requires it). Asks the store for this device's
 * current SSi subscriptions (Premium and India courses) and has the server re-verify each, asking it
 * to move each one to this account (`transfer`, plan 033): whoever holds the
 * paying Apple ID or Google account decides which SSi account has Premium.
 * Returns how many now belong to this account, or why it could not look:
 * 'signed-out' (the server needs an account to give the purchase to),
 * 'cancelled' (the buyer closed the Apple ID prompt), 'failed' (the store or
 * the server did not answer), 'owned-elsewhere' (the server kept a current
 * purchase with another account: a family-shared Apple purchase, a pending
 * Play purchase, or a move that lost to a newer store update, so "no purchase
 * found" would be false).
 *
 * Only this function asks for a move. The silent checks (reconcile, the
 * transaction listener, buyPremium) never do: two SSi accounts used on one
 * phone would otherwise take the subscription from each other on every launch.
 *
 * Only iOS asks the store to sync first (AppStore.sync). The Android plugin's
 * restorePurchases acknowledges every unfinished purchase on the device, which
 * would bypass the server; getPurchases alone lists them without touching them.
 */
export type StoreRestoreResult = number | 'signed-out' | 'cancelled' | 'failed' | 'owned-elsewhere'

export async function restorePurchases(): Promise<StoreRestoreResult> {
  if (!storeBillingLive()) return 'failed'
  if (!(await authToken())) return 'signed-out'
  let store: Awaited<ReturnType<typeof plugin>>['NativePurchases']
  try {
    store = (await plugin()).NativePurchases
    if (storeOs() === 'ios') await store.restorePurchases()
  } catch (err) {
    return classifyPurchaseError(err) === 'cancelled' ? 'cancelled' : 'failed'
  }
  const checked = await verifyCurrentPurchases(store, { transfer: true })
  if (checked === null) return 'failed'
  if (checked.verified === 0 && checked.ownedElsewhere > 0) return 'owned-elsewhere'
  return checked.verified
}

/**
 * Hand every current SSi purchase (Premium and India courses) on this device to the server, and finish
 * the ones it processed. Returns how many now belong to this account, or null
 * when the store would not list them or the server could check none of them.
 * Re-reads the padlock state when any did.
 */
async function verifyCurrentPurchases(
  store: Awaited<ReturnType<typeof plugin>>['NativePurchases'],
  opts: { transfer?: boolean } = {},
): Promise<{ verified: number; ownedElsewhere: number } | null> {
  let purchases: Transaction[]
  try {
    ({ purchases } = await withinStoreTimeout(store.getPurchases({ productType: SUBS, onlyCurrentEntitlements: true })))
  } catch {
    return null
  }
  let verified = 0
  let ownedElsewhere = 0
  let unprocessed = 0
  for (const tx of purchases.filter(isSsiTransaction)) {
    const { processed, owned } = await verifyWithServer(tx, opts)
    await finishIfProcessed(tx, processed)
    if (!processed) unprocessed++
    else if (owned) verified++
    else ownedElsewhere++
  }
  if (verified > 0) await refreshAfterGrant()
  // A purchase the server could not check is a failure to look, not "nothing found".
  return unprocessed > 0 && verified === 0 ? null : { verified, ownedElsewhere }
}

/** Not more often than this on resume; sign-in always checks. */
export const STORE_RECONCILE_INTERVAL_MS = 10 * 60 * 1000
let lastReconcileAt = 0
let reconciling = false

/**
 * Catch the purchases the transaction listener can miss. The plugin starts
 * StoreKit's update stream at app launch, before this page has loaded, and
 * finishes each update before it reports it, so a renewal, an Ask to Buy
 * approval or an interrupted purchase that lands then is reported to nobody
 * and never redelivered. Its entitlement is still current, so listing the
 * current purchases and verifying them heals it. No Apple ID prompt: this
 * never calls AppStore.sync. Signed out it does nothing, because the server
 * has no account to give a purchase to; sign-in runs it again.
 */
export async function reconcileStorePurchases(opts: { force?: boolean } = {}): Promise<void> {
  if (!storeBillingLive() || reconciling) return
  if (!opts.force && Date.now() - lastReconcileAt < STORE_RECONCILE_INTERVAL_MS) return
  if (!(await authToken())) return
  reconciling = true
  try {
    lastReconcileAt = Date.now()
    await verifyCurrentPurchases((await plugin()).NativePurchases)
  } catch {
    // Best effort: the next resume or sign-in tries again.
  } finally {
    reconciling = false
  }
}

/** Never closer together than this, so quick returns to the app ask once (plan 035). */
const STORE_ACCESS_MIN_GAP_MS = 60 * 1000
let lastAccessCheckAt = 0
let recheckingAccess = false

type AccessShape = { provider?: unknown; status?: unknown; cancelAtPeriodEnd?: unknown; currentPeriodEnd?: unknown }

/** What decides a store subscriber's padlock and their Renews or Ends line. */
function accessKey(sub: AccessShape | null | undefined): string {
  return sub ? [sub.provider, sub.status, !!sub.cancelAtPeriodEnd, sub.currentPeriodEnd ?? null].join('|') : 'none'
}

/**
 * One read of /api/subscription that changes nothing. Only a 200 with a readable
 * answer counts. A 401, a 5xx, a timeout, no network, or an answer the server marks
 * storeGrantUnknown is no answer: the shared reads clear the device's copy on a 401,
 * so nothing goes through them unless the answer really changed.
 */
async function probeAccess(token: string): Promise<string | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), STORE_READ_TIMEOUT_MS)
  try {
    const response = await fetch('/api/subscription', {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
    if (response.status !== 200) return null
    const data = (await response.json()) as { subscription?: AccessShape | null; storeGrantUnknown?: boolean } | null
    if (!data || typeof data !== 'object' || !('subscription' in data) || data.storeGrantUnknown) return null
    return accessKey(data.subscription)
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

/**
 * A refund or a revoke reaches the server through the store's notification, never
 * the phone, and the app reads its subscription only at start-up. So on every return
 * to the app a store subscriber's answer is probed. Only an answer that differs from
 * what the app holds re-reads every padlock. The subscription, Settings, the voice and
 * grid padlocks and the cached mirrors then follow at once. The course wall follows at
 * once only if no lesson has made a sound in this app session: after that the
 * entitlement lesson latch (job #860, nothing interrupts learning) holds the old grant
 * until the next start. Do not bypass the latch here. The server already refuses new
 * Premium rounds and audio.
 */
export async function recheckStoreAccess(): Promise<void> {
  if (!storeBillingLive() || recheckingAccess) return
  const provider = useSharedSubscription().subscription.value?.provider
  if (provider !== 'apple' && provider !== 'play') return
  if (Date.now() - lastAccessCheckAt < STORE_ACCESS_MIN_GAP_MS) return
  recheckingAccess = true
  try {
    const token = await authToken()
    if (!token) return
    lastAccessCheckAt = Date.now()
    const answer = await probeAccess(token)
    if (answer === null) {
      // No answer is not "unchanged": let the next return to the app ask again.
      lastAccessCheckAt = 0
      return
    }
    // refreshAfterGrant drops the sign-in boot snapshot, which would answer with the old "yes".
    if (answer !== accessKey(useSharedSubscription().subscription.value)) await refreshAfterGrant()
  } finally {
    recheckingAccess = false
  }
}

/** The store's own manage-subscription page. Cancelling a store purchase happens there, never here. */
export async function openManageSubscriptions(): Promise<void> {
  if (!storeBillingLive()) return
  // A cancel happens on that page, so the next check must not wait for the 10-minute limit.
  // Android leaves the app for Play's page: the return-to-app listener runs the check.
  lastReconcileAt = 0
  try {
    await (await plugin()).NativePurchases.manageSubscriptions()
  } catch {
    // The store would not open its page; nothing else to offer.
    return
  }
  // iOS shows the page as a sheet inside the app and resolves when it closes,
  // so no visibilitychange comes. Check now.
  if (platform().os === 'ios') void reconcileStorePurchases({ force: true })
}

let listening = false

/**
 * Re-verify every transaction update the store reports: renewals, Ask to Buy
 * approvals, a purchase cut off mid-flow. Called once at start-up; does nothing
 * unless storeBillingLive(). The plugin reports these on iOS only; on Android
 * the server hears them from Google directly (play-rtdn). The ones it reports
 * before this listener exists are lost, so start-up, every return to the app
 * and every sign-in also run reconcileStorePurchases().
 */
export function listenForStoreTransactions(): void {
  if (listening || !storeBillingLive()) return
  listening = true
  void (async () => {
    try {
      const store = (await plugin()).NativePurchases
      await store.addListener('transactionUpdated', (tx: Transaction) => {
        if (!isSsiTransaction(tx)) return
        // The plugin has already finished an update it reports; only the grant is left.
        void verifyWithServer(tx).then(({ processed, owned }) => (processed && owned ? refreshAfterGrant() : undefined))
      })
    } catch {
      listening = false
      return
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return
        void reconcileStorePurchases()
        void recheckStoreAccess()
      })
    }
    try {
      // Supabase repeats SIGNED_IN on refocus, so only a different person forces a check.
      let signedInAs: string | null = null
      resolveSupabase()?.auth.onAuthStateChange((event, session) => {
        if (event !== 'SIGNED_IN' || !session?.user?.id || session.user.id === signedInAs) return
        signedInAs = session.user.id
        void reconcileStorePurchases({ force: true })
      })
    } catch {
      // No auth client: the start-up and resume checks still run.
    }
    void reconcileStorePurchases({ force: true })
  })()
}

/** Tests only: forget that the listener was registered. */
export function __resetStoreBillingForTests(): void {
  listening = false
  lastReconcileAt = 0
  reconciling = false
  lastAccessCheckAt = 0
  recheckingAccess = false
  productQuery = Promise.resolve()
}
